import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { Table, TableHead, TableBody, TableRow, TableCell, TableContainer, TablePagination, TextField, Button, Card, CardContent, Typography, CircularProgress } from '@mui/material';

function AllInvoicesAnalysis() {
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [fromDate, setFromDate] = useState(new Date().toISOString().split('T')[0]);  // Default to today's date in YYYY-MM-DD format
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);  // Default to today's date in YYYY-MM-DD format
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [loading, setLoading] = useState(true);  // Added loading state

  // Fetch data from the API
  useEffect(() => {
    axios.get('https://serveremilia.mylksystems.com/sales/InvoiceAnalysis')
      .then(response => {
        setInvoices(response.data);
        setFilteredInvoices(response.data);
        setLoading(false);  // Set loading to false after data is fetched
      })
      .catch(error => {
        console.error('Error fetching invoices:', error);
        setLoading(false);  // Set loading to false even if there's an error
      });
  }, []);

  // Filter invoices based on the invoice number and date range (fromDate and toDate)
  const filterInvoices = () => {
    let filtered = invoices;

    // Filter by invoice number
    if (invoiceNumber) {
      filtered = filtered.filter(invoice => invoice.inv_no.toLowerCase().includes(invoiceNumber.toLowerCase()));
    }

    // Filter by date range
    if (fromDate) {
      filtered = filtered.filter(invoice => invoice.creation_date >= fromDate);
    }

    if (toDate) {
      filtered = filtered.filter(invoice => invoice.creation_date <= toDate);
    }

    setFilteredInvoices(filtered);
  };

  // Handle CSV download
  const downloadCSV = () => {
    const csvData = filteredInvoices.map(invoice => ({
      InvoiceNumber: invoice.inv_no,
      Username: invoice.username,
      ItemName: invoice.item_name,
      Quantity: invoice.qty,
      Total: invoice.total,
      CreationDate: invoice.creation_date.split('T')[0], // Only date part
      BillGross: invoice.bill_gross,
      Discount: invoice.discount,
      NetTotal: invoice.net_total,
      PaymentMethod: invoice.paymentmethod,  // Ensure payment method is included
    }));

    const csvContent = [
      ['InvoiceNumber', 'Username', 'ItemName', 'Quantity', 'Total', 'CreationDate', 'BillGross', 'Discount', 'NetTotal', 'PaymentMethod'],  // Added PaymentMethod here
      ...csvData.map(invoice => Object.values(invoice))
    ]
      .map(row => row.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'invoices.csv');
  };

  return (
    <div>
      <Card>
        <CardContent>
          <Typography variant="h5">Invoice Analysis</Typography>

          {/* Filters */}
          <div style={{ marginBottom: '20px' }}>
            <TextField
              label="Invoice Number"
              variant="outlined"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              style={{ marginRight: '20px' }}
            />
            <TextField
              label="From Date"
              type="date"
              variant="outlined"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              style={{ marginRight: '20px' }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="To Date"
              type="date"
              variant="outlined"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button onClick={filterInvoices} variant="contained" color="primary" style={{ marginLeft: '20px' }}>
              Filter
            </Button>
            <Button onClick={downloadCSV} variant="contained" color="secondary" style={{ marginLeft: '20px' }}>
              Download CSV
            </Button>
          </div>

          {/* Loader while data is loading */}
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
              <CircularProgress />
            </div>
          ) : (
            <>
              {/* Table */}
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Invoice No</TableCell>
                      <TableCell>Username</TableCell>
                      <TableCell>Item Name</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Line Item Total</TableCell>
                      <TableCell>Created Date</TableCell>
                      <TableCell>Bill Gross</TableCell>
                      <TableCell>Discount</TableCell>
                      <TableCell>Net Total</TableCell>
                      <TableCell>Payment Method</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredInvoices.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((invoice) => (
                      <TableRow key={invoice.inv_no}>
                        <TableCell>{invoice.inv_no}</TableCell>
                        <TableCell>{invoice.username}</TableCell>
                        <TableCell>{invoice.item_name}</TableCell>
                        <TableCell>{invoice.qty}</TableCell>
                        <TableCell>{invoice.total}</TableCell>
                        <TableCell>{invoice.creation_date}</TableCell>
                        <TableCell>{invoice.bill_gross}</TableCell>
                        <TableCell>{invoice.discount}</TableCell>
                        <TableCell>{invoice.net_total}</TableCell>
                        <TableCell>{invoice.paymentmethod}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredInvoices.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                onRowsPerPageChange={(event) => setRowsPerPage(parseInt(event.target.value, 10))}
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default AllInvoicesAnalysis;
